import { useMutation, useQuery } from '@tanstack/react-query';
import {
	SovosBox,
	SovosDivider,
	SovosFileUpload,
	SovosMenuItem,
	SovosRadioGroup,
	SovosSelect,
	SovosTableGroup,
	SovosTypography,
} from 'mosaic-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { uploadAddressFile } from 'src/api/addressFile';
import { fetchAllLocationMappings } from 'src/api/locationMappings';
import { fetchLocations } from 'src/api/locations';
import FormStepLayout from 'src/components/FormStepLayout';
import type { Location } from 'src/interfaces/Location';
import type { Response } from 'src/interfaces/Response';
import type { SovosUploadFile } from 'src/interfaces/SovosUploadFile';
import type { NewRateFileFormStep } from 'src/pages/newRateFile/NewRateFileFormTypes';
import type { NewRateFileFormState } from 'src/pages/newRateFile/NewRateFileFormTypes';

const StepLocations: NewRateFileFormStep<{
	selectedLocations: NewRateFileFormState['locations'];
	setSelectedLocations: (locations: NewRateFileFormState['locations']) => void;
	stateLimit: number | null;
}> = ({ onContinue, selectedLocations, setSelectedLocations, stateLimit }) => {
	const locationsQuery = useQuery(['locations'], fetchLocations);

	const mappingsQuery = useQuery(['mappings'], fetchAllLocationMappings);

	const [locationsFiles, setLocationsFiles] = useState<SovosUploadFile[]>(
		() => {
			if (
				selectedLocations?.type === 'upload' &&
				selectedLocations.addressFileName
			) {
				const file = new File(
					[selectedLocations.addressFileName],
					selectedLocations.addressFileName
				) as SovosUploadFile;

				file.progress = { loading: false, percent: 100 };

				return [file];
			}

			return [];
		}
	);

	const uploadFileMutation = useMutation<
		string,
		Response<undefined>,
		{ locationMappingId: number; locationFile: SovosUploadFile }
	>(
		({
			locationMappingId,
			locationFile,
		}: {
			locationMappingId: number;
			locationFile: SovosUploadFile;
		}) => uploadAddressFile(locationMappingId, locationFile),

		{
			onMutate: () => setShowErrors(false),
			onSuccess: (data) => {
				setLocationsFiles((files) => {
					const currentFile = files?.[0];

					if (currentFile) {
						const updatedFile = new File(
							[currentFile],
							currentFile.name
						) as SovosUploadFile;

						updatedFile.progress = { loading: false, percent: 100 };

						if (selectedLocations?.type === 'upload') {
							setSelectedLocations({
								type: 'upload',
								addressFileId: data,
								addressFileName: updatedFile.name,
							});
						}

						return [updatedFile];
					}

					return files;
				});
			},
			onError: () => {
				setLocationsFiles((files) => {
					const currentFile = files?.[0];

					if (currentFile) {
						const updatedFile = new File(
							[currentFile],
							currentFile.name
						) as SovosUploadFile;

						updatedFile.progress = { loading: false, percent: 0 };

						if (selectedLocations?.type === 'upload') {
							setSelectedLocations({
								type: 'upload',
								addressFileId: '',
								addressFileName: '',
							});
						}

						return [updatedFile];
					}

					return files;
				});
				setShowErrors(true);
			},
		}
	);

	const [showErrors, setShowErrors] = useState(false);

	const validations = [
		{
			isValid: selectedLocations?.type,
			message: 'Please select how to add your locations',
		},
		{
			isValid:
				selectedLocations?.type === 'import' ||
				selectedLocations?.type === 'upload' ||
				(selectedLocations?.type === 'select' &&
					selectedLocations.value &&
					selectedLocations.value.length > 0),
			message: 'Please select your locations',
		},
		{
			isValid:
				uploadFileMutation.isError ||
				selectedLocations?.type === 'import' ||
				selectedLocations?.type === 'select' ||
				(selectedLocations?.type === 'upload' &&
					selectedLocations.addressFileId),
			message: 'Please upload your locations',
		},
		{
			isValid:
				selectedLocations?.type === 'import' ||
				selectedLocations?.type === 'select' ||
				(selectedLocations?.type === 'upload' &&
					selectedLocations.locationMapping),
			message: 'Please select a location mapping',
		},
		{
			isValid: !uploadFileMutation.isError || locationsFiles.length === 0,
			message:
				uploadFileMutation.error?.status === 400 &&
				uploadFileMutation.error.message
					? uploadFileMutation.error.message
					: 'Invalid locations file',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	return (
		<FormStepLayout
			title='How would you like to add locations?'
			errors={errors}
			showErrors={showErrors}
			onContinue={handleContinue}
			isLoading={uploadFileMutation.isLoading}
		>
			<SovosRadioGroup
				options={[
					{
						label: 'Import existing locations',
						value: 'import',
					},
					{
						label: 'Select from region list',
						value: 'select',
					},
					{
						label: 'Upload addresses',
						value: 'upload',
					},
				]}
				value={selectedLocations?.type || 'import'}
				onChange={(_: any, value: 'import' | 'select' | 'upload') => {
					setSelectedLocations({ type: value });
					setShowErrors(false);
				}}
			/>

			{selectedLocations?.type && selectedLocations?.type === 'upload' && (
				<>
					<SovosDivider sx={{ my: 3, alignSelf: 'stretch' }} />
					<SovosTypography variant='h5' sx={{ mb: 3 }}>
						Upload addresses
					</SovosTypography>

					<SovosTypography variant='h6' sx={{ mb: 1 }}>
						1. Select location mapping
					</SovosTypography>
					<SovosTypography>
						Select one of your saved location mappings.
					</SovosTypography>
					<SovosTypography>
						Don't see the correct location mapping? Go to{' '}
						<SovosBox
							component={Link}
							to={'/location-mappings'}
							sx={{
								color: 'primary.main',
								textUnderlinePosition: 'under',
							}}
						>
							Location Mappings
						</SovosBox>{' '}
						to create one.
					</SovosTypography>
					<SovosSelect
						required
						data-testid='location-template-select-container'
						inputProps={{ 'data-testid': 'location-template-combo-box' }}
						label={
							<>
								Location mappings<span aria-hidden='true'> *</span>
							</>
						}
						sx={{
							mt: 2,
							maxWidth: '400px',
							'&&& label': {
								color:
									showErrors && !validations[3].isValid
										? 'error.main'
										: undefined,
							},
						}}
						value={selectedLocations.locationMapping?.id}
						onChange={(event) => {
							const locationMapping = mappingsQuery.data?.find(
								(mapping) => Number(event.target.value) === mapping.id
							);

							if (locationMapping) {
								setSelectedLocations({
									...selectedLocations,
									locationMapping,
								});

								if (locationsFiles.length > 0) {
									uploadFileMutation.reset();
									uploadFileMutation.mutate({
										locationMappingId: locationMapping.id,
										locationFile: locationsFiles[0],
									});
								}
							}
						}}
						error={showErrors && !validations[3].isValid}
					>
						{mappingsQuery.isLoading ? (
							<SovosMenuItem disabled value='loading'>
								Loading...
							</SovosMenuItem>
						) : (
							mappingsQuery.data?.map(({ id, name }) => (
								<SovosMenuItem key={id} value={id}>
									{name}
								</SovosMenuItem>
							))
						)}
					</SovosSelect>

					<SovosTypography variant='h6' sx={{ mb: 1 }}>
						2. Upload your file containing your address information
					</SovosTypography>
					<SovosTypography>
						Drag and drop your file in a XLSX format.
					</SovosTypography>
					<SovosFileUpload
						multiple={false}
						disabled={!selectedLocations.locationMapping}
						acceptedTypes='xlsx'
						data-testid='dropzone-location-step'
						fileList={locationsFiles}
						onFilesSelected={(selectedFiles: SovosUploadFile[]) => {
							if (selectedFiles.length > 0) {
								if (selectedLocations.locationMapping) {
									uploadFileMutation.mutate({
										locationMappingId: selectedLocations.locationMapping.id,
										locationFile: selectedFiles[0],
									});
								}

								setLocationsFiles(
									selectedFiles.map((file) => {
										file.progress = { loading: true, percent: null };

										return file;
									})
								);
							}
						}}
						onFileDelete={() => {
							uploadFileMutation.reset();
							setLocationsFiles([]);
							setSelectedLocations({
								...selectedLocations,
								addressFileId: undefined,
								addressFileName: undefined,
							});
							setShowErrors(false);
						}}
						sx={{
							my: 3,
							borderColor:
								showErrors &&
								(!validations[2].isValid || !validations[4].isValid)
									? 'error.main'
									: undefined,
						}}
					/>
				</>
			)}

			{selectedLocations?.type && selectedLocations?.type === 'select' && (
				<>
					<SovosDivider sx={{ my: 3, alignSelf: 'stretch' }} />
					<SovosTypography variant='h5'>Select locations</SovosTypography>
					<SovosTypography sx={{ mb: 3 }}>
						Your rate file will include all tax rates in all jurisdictions in
						the selected regions.
					</SovosTypography>
					<SovosTableGroup
						sx={{
							alignSelf: 'stretch',
							minHeight: '0px',
							'& .sovosTableGroup__tableWrapper': { display: 'flex' },
						}}
						isLoading={locationsQuery.isLoading}
						showNoData={
							!locationsQuery.data || locationsQuery.data.length === 0
						}
						TableProps={{
							fixedHeader: true,
							columns: [
								{
									dataKey: 'name',
									id: 'name',
									title: 'Region',
									width: 'greedy',
								},
								{
									dataKey: 'parentName',
									id: 'parentName',
									title: 'Country',
									width: 'greedy',
								},
							],
							data: locationsQuery.data || [],
							isRowSelectDisabled: (row: Location) => {
								if (
									stateLimit &&
									selectedLocations.value &&
									!selectedLocations.value.find((r) => r.tjId === row.tjId)
								) {
									return selectedLocations.value.length >= stateLimit;
								}
								return false;
							},
							sx: { width: '100%' },
						}}
						onRowSelection={{
							isRowSelected: (row: Location) => {
								return !!selectedLocations.value?.find(
									(r) => r.tjId === row.tjId
								);
							},
							toggleRowSelection: (row: Location) => {
								const selectedRow = selectedLocations.value?.find(
									(r) => r.tjId === row.tjId
								);

								if (selectedLocations.value) {
									if (selectedRow) {
										setSelectedLocations({
											...selectedLocations,
											value: selectedLocations.value?.filter(
												(r) => r.tjId !== selectedRow.tjId
											),
										});
									} else {
										setSelectedLocations({
											...selectedLocations,
											value: [...selectedLocations.value, row],
										});
									}
								} else {
									setSelectedLocations({
										...selectedLocations,
										value: [row],
									});
								}
							},
							areAllRowsSelected: () => {
								return (
									locationsQuery.data &&
									locationsQuery.data.length === selectedLocations.value?.length
								);
							},
							areAnyRowsSelected: () => {
								return (
									selectedLocations.value && selectedLocations.value.length > 0
								);
							},
							selectedCount: selectedLocations.value?.length,
							disableSelectAll: true,
						}}
						bulkActions={[]}
						labels={{
							bulkActions: stateLimit ? `of ${stateLimit} selected` : undefined,
						}}
					/>
					<SovosDivider sx={{ alignSelf: 'stretch' }} />
				</>
			)}
		</FormStepLayout>
	);
};

export default StepLocations;
